import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in ErrorBoundary:', error, errorInfo);
  }

  public handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          minHeight: '200px',
          padding: '2rem',
          background: 'rgba(255, 50, 50, 0.05)',
          border: '1px solid rgba(255, 50, 50, 0.2)',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--text-primary)',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-danger)' }}>
            Something went wrong
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: '400px' }}>
            {this.props.fallbackMessage || this.state.error?.message || 'An unexpected error occurred in this component.'}
          </p>
          <button 
            onClick={this.handleReset}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--surface-raised)',
              border: '1px solid var(--surface-glass-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
